// src/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from "react";
import api from "./api/api";
import { jwtDecode } from "jwt-decode"; // Add this package if not installed: npm install jwt-decode

const AuthContext = createContext();

/**
 * Fetch current user profile using access token (if present).
 */
const fetchUserProfile = async () => {
  const token = localStorage.getItem("access");
  if (!token) return null;

  try {
    const res = await api.get("/api/user-profile/", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err) {
    console.error("[AuthContext] Failed to fetch user profile:", err);
    return null;
  }
};

/**
 * Refresh access token silently using refresh token.
 */
const refreshAccessToken = async () => {
  const refresh = localStorage.getItem("refresh");
  if (!refresh) return null;

  try {
    const res = await api.post("/api/token/refresh/", { refresh });
    const { access } = res.data;
    if (access) {
      localStorage.setItem("access", access);
      console.log("[AuthContext] Access token refreshed.");
      return access;
    }
  } catch (err) {
    console.error("[AuthContext] Silent token refresh failed:", err);
    return null;
  }
  return null;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const loadUser = useCallback(async () => {
    setInitializing(true);
    const profile = await fetchUserProfile();
    setUser(profile);
    setInitializing(false);
  }, []);

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Silent token refresh interval
  useEffect(() => {
    const interval = setInterval(async () => {
      const token = localStorage.getItem("access");
      if (!token) return;

      try {
        const decoded = jwtDecode(token);
        const expTime = decoded.exp * 1000;
        const currentTime = Date.now();

        // If token will expire in the next 60 seconds, refresh it
        if (expTime - currentTime < 60 * 1000) {
          await refreshAccessToken();
        }
      } catch (err) {
        console.error("[AuthContext] Error decoding token:", err);
      }
    }, 4 * 60 * 1000); // Check every 4 minutes

    return () => clearInterval(interval);
  }, []);

  // Listen for auth updates (login, token rotation)
  useEffect(() => {
    const onAuthUpdated = () => loadUser();
    const onForceLogout = () => {
      setUser(null);
      window.location.href = "/login";
    };

    window.addEventListener("auth-updated", onAuthUpdated);
    window.addEventListener("force-logout", onForceLogout);

    return () => {
      window.removeEventListener("auth-updated", onAuthUpdated);
      window.removeEventListener("force-logout", onForceLogout);
    };
  }, [loadUser]);

  const logout = async () => {
    const refresh = localStorage.getItem("refresh");
    try {
      if (refresh) {
        await api.post("/api/logout/", { refresh_token: refresh });
      }
    } catch (err) {
      console.error("[AuthContext] Logout error:", err);
    } finally {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      localStorage.removeItem("user");
      setUser(null);
      window.location.href = "/login";
    }
  };

  const value = {
    user,
    setUser,
    logout,
    initializing,
    refreshUser: loadUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };



